// https://react-spectrum.adobe.com/react-aria/useToast.html

import {memo, useRef} from "react";
import {createPortal} from "react-dom";

import {useToastQueue, ToastState} from "@react-stately/toast";
import type {AriaToastRegionProps} from "@react-aria/toast";
import {useToastRegion} from "@react-aria/toast";
import Toast from "../Toast";
import type {ToastType} from "../Toast";
import {toastQueue} from "./toast-queue";

interface ToastRegionProps extends AriaToastRegionProps {
  state: ToastState<ToastType>;
}

// Toast region component that renders toasts.
const ToastRegion = ({state, ...props}: ToastRegionProps) => {
  const ref = useRef(null);
  const {regionProps} = useToastRegion(props, state, ref);

  return (
    <div
      {...regionProps}
      ref={ref}
      className="group absolute top-0 left-1/2 z-100 flex w-full max-w-[400px] -translate-x-1/2 transform flex-col items-center drop-shadow-2xl [&>*:nth-child(1)]:z-102 [&>*:nth-child(2)]:top-8 [&>*:nth-child(2)]:z-101 [&>*:nth-child(3)]:top-10"
    >
      {state.visibleToasts.map((toast) => (
        <Toast key={toast.key} toast={toast} state={state} />
      ))}
    </div>
  );
};

// Global toast region that creates a portal to render the toast region.
// https://react-spectrum.adobe.com/react-aria/useToast.html#global-toast-queue
const GlobalToastRegion = (props: AriaToastRegionProps) => {
  // Subscribe to it.
  const state = useToastQueue(toastQueue);

  // Render toast region.
  return state.visibleToasts.length > 0
    ? createPortal(
        <ToastRegion {...props} state={state as ToastState<ToastType>} />,
        document.body,
      )
    : null;
};

export default memo(GlobalToastRegion);
